// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { crockfordFromBits, decodeCrockford, encodeCrockford, toHex } from './crockford';

/**
 * `SJ1` = 10 pseudonym characters (50 bits) · `SJ2` = 13 (65 bits).
 *
 * `PERSON_0f3a` — four hex characters, 16 bits — was the original sketch and is unshippable:
 * the 50% collision point is 301 distinct values, and at 10,000 entities you expect ~763
 * colliding pairs. That would merge distinct customers into one pseudonym, which both poisons
 * the training data and makes the mapping one-to-many, falsifying the product's headline claim.
 * At 50 bits, 200,000 distinct entities collide with probability ~1e-4.
 */
export type Profile = 'SJ1' | 'SJ2';

export const PROFILE_CHARS: Readonly<Record<Profile, number>> = { SJ1: 10, SJ2: 13 };

const SECRET_BYTES = 20;

export function newProjectSecret(): Uint8Array {
  const b = new Uint8Array(SECRET_BYTES);
  crypto.getRandomValues(b);
  return b;
}

const enc = new TextEncoder();

async function hmac(keyBytes: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', k, message as BufferSource));
}

function label(text: string): Uint8Array {
  const l = enc.encode(text);
  const out = new Uint8Array(l.length + 1);
  out.set(l, 0);
  out[l.length] = 0x01;
  return out;
}

/**
 * Derive the pseudonym key.
 *
 * This is HKDF-Expand with L = 32, hand-rolled in four lines rather than via
 * `deriveKey({name:'HKDF'})`, because Firefox only shipped the derive-into-HMAC case in 119 and
 * a single `subtle.sign('HMAC', ...)` is universally supported and cryptographically identical
 * for full-entropy 160-bit input keying material.
 *
 * The result is imported `extractable: false` and handed to workers as a `CryptoKey` object, so
 * raw secret bytes never enter a worker heap.
 */
export async function pseudonymKey(secret: Uint8Array): Promise<CryptoKey> {
  const raw = await hmac(secret, label('scrubjay/v1/pseudonym'));
  return crypto.subtle.importKey('raw', raw as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
}

/**
 * Eight hex characters identifying the project.
 *
 * Not invertible, but it DOES link two mapping files to the same project — which is stated in
 * the Privacy modal rather than left for someone to discover.
 */
export async function keyFingerprint(secret: Uint8Array): Promise<string> {
  const raw = await hmac(secret, label('scrubjay/v1/keycheck'));
  return toHex(raw.subarray(0, 4));
}

/** First 20 bits of SHA-256(secret), as four Crockford characters. */
async function checksum(secret: Uint8Array): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest('SHA-256', secret as BufferSource));
  const bits = ((d[0]! << 12) | (d[1]! << 4) | (d[2]! >> 4)) & 0xfffff;
  return crockfordFromBits(bits, 4);
}

/** `SJ1-Z86D-BEHA-KPNP-E6QP-1V1M-9BTS-7FEN-ZK74-Q26X` — 36 payload characters in nine groups. */
export async function formatProjectKey(secret: Uint8Array, profile: Profile): Promise<string> {
  const body = encodeCrockford(secret) + (await checksum(secret));
  const groups: string[] = [];
  for (let i = 0; i < body.length; i += 4) groups.push(body.slice(i, i + 4));
  return `${profile}-${groups.join('-')}`;
}

export interface ParsedProjectKey {
  readonly secret: Uint8Array;
  readonly profile: Profile;
  readonly tokenChars: number;
}

export async function parseProjectKey(text: string): Promise<ParsedProjectKey> {
  const trimmed = text.trim().toUpperCase();
  const m = /^(SJ1|SJ2)-([0-9A-Z-]+)$/.exec(trimmed);
  if (!m) throw new Error('That does not look like a scrubjay project key.');
  const profile = m[1] as Profile;
  const body = m[2]!.replace(/-/g, '');
  if (body.length !== 36) {
    throw new Error(`A project key has 36 characters after the prefix; this one has ${body.length}.`);
  }
  const secret = decodeCrockford(body.slice(0, 32)).subarray(0, SECRET_BYTES);
  const expected = await checksum(secret);
  if (body.slice(32) !== expected) {
    throw new Error('Checksum failed — the key has a typo in it.');
  }
  return { secret: new Uint8Array(secret), profile, tokenChars: PROFILE_CHARS[profile] };
}
